<script setup lang="ts">
import { ref } from 'vue'
import { AdminDataTable, AdminFilterBar, AdminStatusTag, type TableColumnType } from '@xjlv2/admin-layout'
import type { ShellNotification } from '../../types'
import type { NotificationListQuery } from '../../composables/useNotificationCenter'

const props = defineProps<{
  notifications: ShellNotification[]
  total: number
  page: number
  pageSize: number
  loading: boolean
  error: string
}>()

const emit = defineEmits<{
  open: [notification: ShellNotification]
  queryChange: [query: NotificationListQuery]
  markAllRead: []
  remove: [id: number]
  clear: []
}>()

const typeFilter = ref('')
const readFilter = ref('')

const columns: TableColumnType<ShellNotification>[] = [
  { key: 'title', title: '标题', minWidth: 260, ellipsis: true },
  { key: 'type', title: '类型', width: 100, align: 'center' },
  { key: 'sender', title: '发送者', minWidth: 140, ellipsis: true },
  { key: 'createdAt', title: '发送时间', width: 150 },
  { key: 'read', title: '阅读状态', width: 100, align: 'center' },
  { key: 'actions', title: '操作', width: 150, align: 'right', fixed: 'right', disabled: true }
]

function query(pageNo = 1, pageSize = props.pageSize) {
  emit('queryChange', {
    type: typeFilter.value,
    read: readFilter.value === '' ? null : readFilter.value === 'true',
    pageNo,
    pageSize
  })
}

function reset() {
  typeFilter.value = ''
  readFilter.value = ''
  query()
}

function typeLabel(type: string) {
  const labels: Record<string, string> = { INFO: '通知', SUCCESS: '成功', WARNING: '提醒', TASK: '待办' }
  return labels[type] ?? type
}

function formatTime(value: string) {
  return new Intl.DateTimeFormat('zh-CN', {
    month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit'
  }).format(new Date(value))
}
</script>

<template>
  <section class="panel span notification-center">
    <AdminFilterBar :loading="loading" @submit="query()" @reset="reset">
      <select v-model="typeFilter" aria-label="通知类型">
        <option value="">全部类型</option>
        <option value="TASK">待办</option>
        <option value="INFO">通知</option>
        <option value="SUCCESS">成功</option>
        <option value="WARNING">提醒</option>
      </select>
      <select v-model="readFilter" aria-label="阅读状态">
        <option value="">全部状态</option>
        <option value="false">未读</option>
        <option value="true">已读</option>
      </select>
      <template #actions>
        <button type="submit" class="primary">查询</button>
        <button type="button" class="secondary" :disabled="loading" @click="reset">重置</button>
        <button type="button" class="secondary" :disabled="loading" @click="emit('markAllRead')">全部已读</button>
        <button type="button" class="secondary danger" :disabled="loading" @click="emit('clear')">清空</button>
      </template>
    </AdminFilterBar>

    <AdminDataTable
      :columns="columns"
      :data-source="notifications"
      :loading="loading"
      :error="error"
      :current="page"
      :page-size="pageSize"
      :total="total"
      :show-column-setting="true"
      @refresh="query(page)"
      @update:current="query($event)"
      @update:page-size="query(1, $event)"
    >
      <template #bodyCell="{ column, record }">
        <template v-if="column.key === 'title'">
          <span class="notification-title-cell">
            <strong>{{ record.title }}</strong>
            <small>{{ record.body || record.linkText || '-' }}</small>
          </span>
        </template>
        <AdminStatusTag v-else-if="column.key === 'type'" :text="typeLabel(record.type)" tone="info" />
        <template v-else-if="column.key === 'createdAt'">{{ formatTime(record.createdAt) }}</template>
        <AdminStatusTag
          v-else-if="column.key === 'read'"
          :text="record.read ? '已读' : '未读'"
          :tone="record.read ? 'neutral' : 'primary'"
        />
        <span v-else-if="column.key === 'actions'" class="row-actions compact-actions">
          <button type="button" class="link-action" @click="emit('open', record)">{{ record.linkText || '查看' }}</button>
          <button type="button" class="link-action danger-text" @click="emit('remove', record.id)">删除</button>
        </span>
      </template>
    </AdminDataTable>
  </section>
</template>

<style scoped>
.notification-title-cell {
  display: grid;
  gap: var(--rr-space-1);
  min-width: 0;
}

.notification-title-cell small {
  overflow: hidden;
  color: var(--rr-color-text-muted);
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
